import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

/**
 * Dialog component for sharing event details on social media
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.handleClose - Function to close the dialog
 * @param {string} props.title - Event title
 * @param {string} props.description - Event description for sharing
 * @param {string} props.eventId - Event ID for fetching images
 * @param {string} props.role - User role for the event
 * @param {Object} props.registrationData - Optional registration data
 */
const ShareDialog = ({
  open,
  handleClose,
  title,
  description,
  eventId,
  role = "attendee",
  registrationData = null,
}) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Generate the social sharing URL for this event
  const shareUrl = `${window.location.origin}/events/${eventId}/${role}`;

  // Social media images with fallback to generic event image
  const socialImages = {
    twitter: `https://eventegration.com/posts/${eventId}/twitter.jpeg`,
    facebook: `https://eventegration.com/posts/${eventId}/facebook.jpeg`,
  };

  // Prepare share content
  const shareContent = `${description || "Join this event!"} ${shareUrl}`;
  const encodedShareContent = encodeURIComponent(shareContent);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(
    description || "Join this event!"
  );

  // Social media share URLs
  const shareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedDescription}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedShareContent}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodedShareContent}`,
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleImageError = (e) => {
    if (!imageError) {
      e.target.src = socialImages.facebook;
      setImageError(true);
    } else {
      e.target.src = "https://eventegration.com/default-event-image.jpg";
    }
  };

  // Open the dedicated social share page
  const handleOpenSocialPage = () => {
    handleClose();
    // Pass registration data via state if available
    navigate(`/socialShare?eventId=${eventId}&role=${role}`, {
      state: {
        registrationData: registrationData
          ? { ...registrationData, role }
          : null,
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center">
          <ShareIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Share this event</Typography>
        </Box>
        <IconButton onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardMedia
            component="img"
            height="200"
            image={socialImages.twitter}
            alt={title}
            onError={handleImageError}
          />
          <CardContent>
            <Typography gutterBottom variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", mb: 3, gap: 1.5 }}>
          <TextField
            fullWidth
            value={shareContent}
            InputProps={{ readOnly: true }}
          />
          <Button
            variant="contained"
            onClick={handleCopyLink}
            startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
          >
            {copied ? "Copied!" : "Copy"}
          </Button>
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            mb: 2,
            color: "text.primary",
            fontWeight: 500,
          }}
        >
          Share to Social Platforms
        </Typography>

        <Grid container spacing={2}>
          {Object.entries({
            facebook: <FacebookIcon />,
            twitter: <TwitterIcon />,
            linkedin: <LinkedInIcon />,
            whatsapp: <WhatsAppIcon />,
          }).map(([platform, icon]) => (
            <Grid item xs={6} sm={3} key={platform}>
              <Button
                fullWidth
                variant="contained"
                startIcon={icon}
                component="a"
                href={shareUrls[platform]}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  py: 1.5,
                  textTransform: "capitalize",
                  fontSize: "0.875rem",
                  "&:hover": {
                    boxShadow: 2,
                    transform: "translateY(-2px)",
                  },
                  ...(platform === "facebook" && {
                    bgcolor: "#1877F2",
                    "&:hover": { bgcolor: "#166FE5" },
                  }),
                  ...(platform === "twitter" && {
                    bgcolor: "#000000",
                    "&:hover": { bgcolor: "#1C1C1C" },
                  }),
                  ...(platform === "linkedin" && {
                    bgcolor: "#0A66C2",
                    "&:hover": { bgcolor: "#094582" },
                  }),
                  ...(platform === "whatsapp" && {
                    bgcolor: "#25D366",
                    "&:hover": { bgcolor: "#1DA851" },
                  }),
                }}
              >
                {platform}
              </Button>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenSocialPage}
          sx={{ py: 1.5 }}
        >
          Open Dedicated Share Page
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
